import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function formatFactor(value) {
  return value < 0 ? `(${value})` : `${value}`;
}

function operationLatex(data) {
  return `${formatFactor(data.a)}\\cdot ${formatFactor(data.b)}`;
}

function makeTask(type, a, b, note) {
  return {
    type,
    data: {
      a,
      b,
      result: a * b,
      note,
    },
    tags: ["liczby_calkowite", "mnozenie"],
  };
}

function positiveByPositive() {
  const a = randInt(2, 9);
  const b = randInt(2, 9);
  return makeTask(
    "IntegerMulPositiveByPositiveTask",
    a,
    b,
    "To przypomnienie tabliczki mnożenia: dwa dodatnie czynniki dają dodatni iloczyn."
  );
}

function negativeByPositive() {
  const a = -randInt(2, 9);
  const b = randInt(2, 9);
  return makeTask(
    "IntegerMulNegativeByPositiveTask",
    a,
    b,
    "Jedna liczba ujemna i jedna dodatnia dają wynik ujemny."
  );
}

function positiveByNegative() {
  const a = randInt(2, 12);
  const b = -randInt(2, 9);
  return makeTask(
    "IntegerMulPositiveByNegativeTask",
    a,
    b,
    "Mnożenie jest przemienne, więc dodatnia razy ujemna też daje wynik ujemny."
  );
}

function negativeByNegative() {
  const a = -randInt(2, 12);
  const b = -randInt(2, 9);
  return makeTask(
    "IntegerMulNegativeByNegativeTask",
    a,
    b,
    "Dwie liczby ujemne w mnożeniu dają wynik dodatni."
  );
}

function twoDigitByOneDigitSigned() {
  const twoDigit = randInt(12, 49);
  const oneDigit = randInt(2, 9);
  const negativeSide = randInt(0, 1);
  const a = negativeSide === 0 ? -twoDigit : twoDigit;
  const b = negativeSide === 1 ? -oneDigit : oneDigit;
  return makeTask(
    "IntegerMulTwoDigitByOneDigitSignedTask",
    a,
    b,
    "Najpierw mnożymy wartości bez znaków, a potem ustalamy znak wyniku."
  );
}

function twoDigitByTwoDigitSigned() {
  const first = randInt(12, 49);
  const second = randInt(12, 49);
  const signVariant = randInt(0, 2);
  const a = signVariant === 0 || signVariant === 2 ? -first : first;
  const b = signVariant === 1 || signVariant === 2 ? -second : second;
  return makeTask(
    "IntegerMulTwoDigitByTwoDigitSignedTask",
    a,
    b,
    "To trudniejszy wariant: obie wartości są dwucyfrowe, a znak zależy od liczby minusów."
  );
}

export function generateTasks() {
  return [
    positiveByPositive(),
    negativeByPositive(),
    positiveByNegative(),
    negativeByNegative(),
    twoDigitByOneDigitSigned(),
    twoDigitByTwoDigitSigned(),
  ];
}

export function renderTask({ data }) {
  return (
    <p>
      Oblicz: <InlineMath math={operationLatex(data)} />.
    </p>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="wynik"
      placeholder="np. -24"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const value = userAnswer?.parsed;
  return Number.isFinite(value) && value === data.result;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.result}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

function signExplanation(data) {
  const absA = Math.abs(data.a);
  const absB = Math.abs(data.b);
  const absoluteProduct = absA * absB;

  if (data.a > 0 && data.b > 0) {
    return [
      <span>
        Mnożymy jak w tabliczce mnożenia:{" "}
        <InlineMath math={`${data.a}\\cdot ${data.b}=${data.result}`} />.
      </span>,
    ];
  }

  if (data.a < 0 && data.b < 0) {
    return [
      <span>
        Najpierw mnożymy wartości bez znaków:{" "}
        <InlineMath math={`${absA}\\cdot ${absB}=${absoluteProduct}`} />.
      </span>,
      <span>Dwa minusy w mnożeniu dają plus, więc wynik jest dodatni.</span>,
    ];
  }

  return [
    <span>
      Najpierw mnożymy wartości bez znaków:{" "}
      <InlineMath math={`${absA}\\cdot ${absB}=${absoluteProduct}`} />.
    </span>,
    <span>Jeden czynnik jest ujemny, więc iloczyn jest ujemny.</span>,
  ];
}

export function renderExplanation({ data }) {
  const steps = signExplanation(data);
  return (
    <div style={{ textAlign: "left" }}>
      <p>{data.note}</p>
      {steps.map((step, index) => (
        <p key={index}>{step}</p>
      ))}
      <p>
        <strong>Wynik:</strong>{" "}
        <InlineMath math={`${operationLatex(data)}=${data.result}`} />.
      </p>
    </div>
  );
}

export const registry = {
  IntegerMulPositiveByPositiveTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
  IntegerMulNegativeByPositiveTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
  IntegerMulPositiveByNegativeTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
  IntegerMulNegativeByNegativeTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
  IntegerMulTwoDigitByOneDigitSignedTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
  IntegerMulTwoDigitByTwoDigitSignedTask: {
    renderTask,
    renderInput,
    checkAnswer,
    renderAnswer,
    renderUserAnswer,
    renderExplanation,
  },
};
